import React from "react"
import { motion } from "framer-motion"
import { BookOpen, GraduationCap, Headphones, PenTool, MessageCircle, BarChart, Users, Laptop, CheckCircle, Clock } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const TestPreparation = () => {
  const testTracks = [
    { icon: <MessageCircle />, title: "IELTS", desc: "Expert training for Academic and General tracks with focus on band scores." },
    { icon: <Laptop />, title: "TOEFL", desc: "Computer-based testing simulations for university admissions worldwide." },
    { icon: <BarChart />, title: "GRE", desc: "Advanced Verbal and Math prep for prestigious graduate programs." },
    { icon: <GraduationCap />, title: "GMAT", desc: "Strategic management testing prep for top-tier business schools." }
  ]

  const classFormats = [
    { icon: <Users />, title: "Group Classes", desc: "Interactive sessions with peer learning and competition." },
    { icon: <PenTool />, title: "One-on-One", desc: "Personalized focus on your specific weak areas." },
    { icon: <Laptop />, title: "Hybrid/Online", desc: "Live virtual classes with recorded playback options." },
    { icon: <BookOpen />, title: "Self-Prep Kits", desc: "Curated study materials for independent learners." }
  ]

  return (
    <Layout>
      <SEO
        title="Test Preparation - IELTS, TOEFL & More | Welsh Education Group"
        description="Expert test preparation services for IELTS, TOEFL, GRE, GMAT, and other standardized tests."
        url="/services/test-prep"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="hero-icon-large"
            >
              <BookOpen size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Test Preparation
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Master standardized testing with our elite coaching and personalized study architecture.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>Score Maximization</h2>
                <p>
                  Achieving a high score in standardized tests requires more than just knowledge—it requires strategy, time management, and thousands of practice hours. Our instructors are top-tier scorers who provide the techniques needed to excel.
                </p>

                <div className="service-features-list">
                  {testTracks.map((track, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{track.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{track.title}</h4>
                        <p>{track.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="formats-section" style={{ marginTop: '80px' }}>
                  <h2 style={{ marginBottom: '40px' }}>Learning Formats</h2>
                  <div className="info-grid">
                    {classFormats.map((format, idx) => (
                      <div key={idx} className="info-card">
                        <div style={{ color: 'var(--cyan-glow)', marginBottom: '16px' }}>{format.icon}</div>
                        <h3>{format.title}</h3>
                        <p style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>{format.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  className="sidebar-cta-card"
                >
                  <Clock className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Trial Session</h3>
                  <p>Experience our teaching methodology with a free mock lesson.</p>
                  <button className="sidebar-btn highlight-glow">Book Free Mock</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Our Track Record</h3>
                  <ul className="stats-list" style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Success Rate</span>
                      <span className="text-gradient" style={{ fontWeight: 800 }}>95%</span>
                    </li>
                    <li style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Avg. Band Gain</span>
                      <span className="text-gradient" style={{ fontWeight: 800 }}>+1.5</span>
                    </li>
                    <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted-foreground)' }}>Trained</span>
                      <span className="text-gradient" style={{ fontWeight: 800 }}>5K+</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default TestPreparation
